/**
 * Google Analytics 4 Helper
 * Centralne API do obsługi wszystkich eventów GA4 w aplikacji
 */

// ** Imports
import { hasAnalyticsConsent } from "./hasAnalyticsConsent";

// Sprawdza czy Google Analytics jest dostępne
const isGtagAvailable = () => {
	return (
		typeof window !== "undefined" &&
		window.gtag &&
		typeof window.gtag === "function"
	);
};

// Podstawowa funkcja do wysyłania eventów
const trackEvent = (eventName, parameters = {}) => {
	if (!isGtagAvailable()) {
		// eslint-disable-next-line no-console
		console.warn("Google Analytics (gtag) is not available");
		return false;
	}

	try {
		window.gtag("event", eventName, {
			...parameters,
			// Dodajemy timestamp dla lepszego trackingu
			timestamp: Date.now(),
		});

		// Debug log w development
		if (process.env.NODE_ENV === "development") {
			// eslint-disable-next-line no-console
			console.log("GA4 Event tracked:", eventName, parameters);
		}

		return true;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn("Analytics trackEvent error:", error);
		return false;
	}
};

// #####################################################################

// ############### AUTH EVENTS ###############

// ** Event: user_login_success
export const trackUserLoginSuccess = ({
	method = "email",
	userId = null,
	provider = null,
} = {}) => {
	const params = {
		event_category: "auth",
		event_label: "web_login",
		method,
		login_provider: provider,
	};

	if (hasAnalyticsConsent() && userId) {
		params.user_id = userId.toString();
	}

	return trackEvent("user_login_success", params);
};

// ** Event: user_login_failure
export const trackUserLoginFailure = ({ userId = null } = {}) => {
	const params = {
		event_category: "auth",
		event_label: "web_logout",
	};

	if (hasAnalyticsConsent() && userId) {
		params.user_id = userId.toString();
	}

	return trackEvent("user_login_failure", params);
};

// ** Event: view page for logged user
export const trackViewPageForLoggedUser = ({
	userId = null,
	delay = 300,
} = {}) => {
	const executeTracking = () => {
		const params = {
			event_category: "auth",
			event_label: "web_view_page_for_logged_user",
		};

		if (hasAnalyticsConsent() && userId) {
			params.user_id = userId.toString();
		}

		return trackEvent("view_page_for_logged_user", params);
	};

	// Opóźnienie pozwala na aktualizację consent w dataLayer
	if (delay > 0) {
		setTimeout(executeTracking, delay);
		return true; // Zwracamy true bo event zostanie wysłany
	} else {
		return executeTracking();
	}
};

// ** Event: click oauth button
export const trackClickOAuthButton = ({
	method = "oauth",
	provider = null,
} = {}) => {
	const params = {
		event_category: "auth",
		event_label: "web_click_oauth_button",
		method,
		provider,
	};

	return trackEvent("click_oauth_button", params);
};

// ** Event: registration redirect after oauth
export const trackUserRegistrationRedirectAfterOAuth = ({
	method = "oauth",
	provider = null,
	userId = null,
} = {}) => {
	const params = {
		event_category: "auth",
		event_label: "web_registration_redirect_after_oauth",
		method,
		provider,
	};

	if (hasAnalyticsConsent() && userId) {
		params.user_id = userId.toString();
	}

	return trackEvent("registration", params);
};

// ** Event: open registration form
export const trackOpenRegistrationForm = ({
	method = "email",
	userId = null,
} = {}) => {
	const params = {
		event_category: "auth",
		event_label: "web_registration",
		method,
	};

	if (hasAnalyticsConsent() && userId) {
		params.user_id = userId.toString();
	}

	return trackEvent("open_registration_form", params);
};

// ** Event: registration step 1 success
export const trackUserRegistrationStep1 = ({
	method = "email",
	userId = null,
} = {}) => {
	const params = {
		event_category: "auth",
		event_label: "web_registration",
		method,
	};

	if (hasAnalyticsConsent() && userId) {
		params.user_id = userId.toString();
	}

	return trackEvent("registration_step_1_success", params);
};

// ** Event: registration step 2 success
export const trackUserRegistrationStep2 = ({
	method = "email",
	userId = null,
} = {}) => {
	const params = {
		event_category: "auth",
		event_label: "web_registration",
		method,
	};

	if (hasAnalyticsConsent() && userId) {
		params.user_id = userId.toString();
	}

	return trackEvent("registration_step_2_success", params);
};

// ** Event: registration step 3 success
export const trackUserRegistrationStep3 = ({
	method = "email",
	userId = null,
} = {}) => {
	const params = {
		event_category: "auth",
		event_label: "web_registration",
		method,
	};

	if (hasAnalyticsConsent() && userId) {
		params.user_id = userId.toString();
	}

	return trackEvent("registration_step_3_success", params);
};

// ** Event: registration step 4 success
export const trackUserRegistrationStep4 = ({
	method = "email",
	userId = null,
} = {}) => {
	const params = {
		event_category: "auth",
		event_label: "web_registration",
		method,
	};

	if (hasAnalyticsConsent() && userId) {
		params.user_id = userId.toString();
	}

	return trackEvent("registration_step_4_success", params);
};

// ** Event: registration step 5 success
export const trackUserRegistrationStep5 = ({
	method = "email",
	userId = null,
} = {}) => {
	const params = {
		event_category: "auth",
		event_label: "web_registration",
		method,
	};

	if (hasAnalyticsConsent() && userId) {
		params.user_id = userId.toString();
	}

	return trackEvent("registration_step_5_success", params);
};

// ** Event: registration success
export const trackUserRegistrationSuccess = ({
	method = "email",
	userId = null,
} = {}) => {
	const params = {
		event_category: "auth",
		event_label: "web_registration",
		method,
	};

	if (hasAnalyticsConsent() && userId) {
		params.user_id = userId.toString();
	}

	return trackEvent("registration", params);
};

// ** Event: registration email verification success
export const trackUserRegistrationEmailVerificationSuccess = ({
	method = "email",
	userId = null,
} = {}) => {
	const params = {
		event_category: "auth",
		event_label: "web_registration",
		method,
	};

	if (hasAnalyticsConsent() && userId) {
		params.user_id = userId.toString();
	}

	return trackEvent("registration_email_verification_success", params);
};

// ############### PURCHASE EVENTS ###############

// ** Event: open payment page
export const trackOpenPaymentPage = ({ userId = null } = {}) => {
	const params = {
		event_category: "purchase",
		event_label: "web_open_payment_page",
	};

	if (hasAnalyticsConsent() && userId) {
		params.user_id = userId.toString();
	}

	return trackEvent("open_payment_page", params);
};

// ** Event: select payment method
export const trackSelectPaymentMethod = ({
	userId = null,
	selectedMethod = null,
	selectedAmount = null,
	selectedCurrency = null,
	selectedDays = null,
	selectedSuperVip = null,
} = {}) => {
	const params = {
		event_category: "purchase",
		event_label: "web_select_payment_method",
		selected_method: selectedMethod,
		selected_amount: selectedAmount,
		selected_currency: selectedCurrency,
		selected_days: selectedDays,
		selected_super_vip: selectedSuperVip,
	};

	if (hasAnalyticsConsent() && userId) {
		params.user_id = userId.toString();
	}

	return trackEvent("select_payment_method", params);
};

// #####################################################################

// Eksportujemy również podstawową funkcję
export { trackEvent };

// Default export z wszystkimi funkcjami
export default {
	trackEvent,
	trackUserLoginSuccess,
	trackUserLoginFailure,
	trackViewPageForLoggedUser,
	trackClickOAuthButton,
	trackUserRegistrationRedirectAfterOAuth,
	trackOpenRegistrationForm,
	trackUserRegistrationStep1,
	trackUserRegistrationStep2,
	trackUserRegistrationStep3,
	trackUserRegistrationStep4,
	trackUserRegistrationStep5,
	trackUserRegistrationSuccess,
	trackOpenPaymentPage,
	trackSelectPaymentMethod,
	isGtagAvailable,
	hasAnalyticsConsent,
};

// #####################################################################
